import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, tap, filter, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import * as SpareAllocationActions from './spare-allocation.actions';
import * as ServiceRequestActions from '../service-request/service-request.actions';
import * as ServiceRequestSelectors from '../service-request/service-request.selectors';
import * as JobCardActions from '../job-card/job-card.actions';
import { SpareAllocationService } from '../../../../core/services/gse/spare-allocation.service';
import { ToastService } from '../../../../core/services/toast.service';

@Injectable()
export class SpareAllocationEffects {
  private actions$ = inject(Actions);
  private service = inject(SpareAllocationService);
  private toastService = inject(ToastService);
  private store = inject(Store);

  loadRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SpareAllocationActions.loadSpareAllocationRequests),
      switchMap(({ params }) =>
        this.service.getAll(params).pipe(
          map((response) =>
            SpareAllocationActions.loadSpareAllocationRequestsSuccess({
              requests: response.body?.data || [],
              page: response.body?.page,
            })
          ),
          catchError((error) =>
            of(
              SpareAllocationActions.loadSpareAllocationRequestsFailure({
                error:
                  error.error?.message || error.message || 'Failed to load spare part requests',
              })
            )
          )
        )
      )
    )
  );

  loadDetailsOnSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SpareAllocationActions.selectSpareAllocationRequest),
      filter(({ id }) => id !== null),
      map(({ id }) => SpareAllocationActions.loadSpareAllocationDetails({ id: id! }))
    )
  );

  loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SpareAllocationActions.loadSpareAllocationDetails),
      switchMap(({ id }) =>
        this.service.getDetails(id).pipe(
          map((response) => {
            const details = response.body?.data?.[0];
            if (!details) {
              return SpareAllocationActions.loadSpareAllocationDetailsFailure({
                error: 'Request details not found',
              });
            }
            return SpareAllocationActions.loadSpareAllocationDetailsSuccess({ details });
          }),
          catchError((error) =>
            of(
              SpareAllocationActions.loadSpareAllocationDetailsFailure({
                error: error.error?.message || error.message || 'Failed to load request details',
              })
            )
          )
        )
      )
    )
  );

  allocate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SpareAllocationActions.allocateSpares),
      switchMap(({ id, body }) =>
        this.service.allocate(id, body).pipe(
          map((response) => {
            const details = response.body?.data?.[0] ?? {};
            return SpareAllocationActions.allocateSparesSuccess({ details });
          }),
          catchError((error) =>
            of(
              SpareAllocationActions.allocateSparesFailure({
                error: error.error?.message || error.message || 'Failed to allocate spare parts',
              })
            )
          )
        )
      )
    )
  );

  allocateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SpareAllocationActions.allocateSparesSuccess),
        tap(() => this.toastService.showSuccess('Success', 'Spare parts allocated successfully'))
      ),
    { dispatch: false }
  );

  reloadJobCardDetailAfterAllocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SpareAllocationActions.allocateSparesSuccess),
      filter(({ details }) => !!details.jobCard?.id),
      map(({ details }) =>
        JobCardActions.loadJobCardById({ params: { id: details.jobCard!.id! } })
      )
    )
  );

  reloadJobCardsAfterAllocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SpareAllocationActions.allocateSparesSuccess),
      withLatestFrom(this.store.select(ServiceRequestSelectors.selectLastJobCardParams)),
      map(([, lastParams]) =>
        ServiceRequestActions.loadJobCards({
          params: lastParams ?? {},
          preserveSelection: true,
          silent: true,
        })
      )
    )
  );

  allocateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SpareAllocationActions.allocateSparesFailure),
        tap(({ error }) => this.toastService.showError('Error', error))
      ),
    { dispatch: false }
  );

}
